import {
  useReducer, useCallback, useRef, useMemo,
} from 'react';

import cardValidator from 'card-validator';
import Router from 'next/router';
import { isNumber } from 'utils';
import {  capitalize } from 'utils/string';

import { UnprocessableError } from '../errors';

const emailRegExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const validations = {
  card: (value) => cardValidator.number(value).isValid,
  cardExpiration: (value) => cardValidator.expirationDate(value).isValid,
  cvc: (value) => cardValidator.cvv(value, Math.max(3, Math.min(4, value.length))).isValid,
  equal: (value, field, { values }) => value === values[field],
  email: (value) => emailRegExp.test(value),
  minLength: (value, length) => value.length >= length,
  maxLength: (value, length) => value.length < length,
  required: (value) => value !== false && value != null && (typeof value !== 'string' || value.trim().length),
  number: isNumber,
  lessThan: (value, maxValue) => +value < +maxValue,
  moreThan: (value, minValue) => +value > +minValue,
  date: (value) => {
    const date = new Date(value);
    return !Number.isNaN(date.getDate());
  },
};
const errorMessages = {
  email: 'This value should be a valid email address',
  card: 'This value should be a valid card',
  cardExpiration: 'This value should be a valid card expiration date',
  cvc: 'This value should be a valid card CVC',
  equal: (fieldName, equalTo) => `${fieldName} should be equal to ${equalTo}`,
  minLength: (fieldName, fieldValue) => `${capitalize(fieldName)} should be at least ${fieldValue} chars long`,
  maxLength: (fieldName, fieldValue) => `${capitalize(fieldName)} should be less than ${fieldValue} chars long`,
  required: (fieldName) => `${capitalize(fieldName)} is required`,
  lessThan: (fieldName, fieldValue) => `${fieldName} should be less than ${fieldValue}`,
  moreThan: (fieldName, fieldValue) => `${fieldName} should be more than ${fieldValue}`,
  number: (fieldName) => `${fieldName} should be a number`,
  date: (fieldName) => `${fieldName} should be a valid date`,
};
const reducer = (state, action) => {
  switch (action.type) {
  case 'change': {
    if (state.values[action.name] === undefined) {
      return state;
    }
    return {
      ...state,
      values: {
        ...state.values,
        [action.name]: action.value,
      },
      errors: {
        ...state.errors,
        [action.name]: '',
        _error: '',
      },
    };
  }
  case 'resetErrors': {
    return {
      ...state,
      errors: {},
    };
  }
  case 'setErrors': {
    return {
      ...state,
      errors: {
        ...action.errors,
      },
    };
  }
  case 'setIsLoading': {
    return {
      ...state,
      isLoading: action.payload,
    };
  }
  case 'setInitialState': {
    return action.payload;
  }
  default:
    return state;
  }
};

export function validateBySchema(schema, values, options = {}) {
  const errors = Object.keys(schema)
    .map((key) => {
      const exclude = options?.exclude?.includes?.(key);
      const skip = options?.skipMissing && !Object.prototype.hasOwnProperty.call(values, key);
      if (exclude || skip) {
        return null;
      }
      const rules = schema[key]?.rules || [];
      const displayName = schema[key]?.displayName || key;
      if (!rules.length) {
        return null;
      }
      const failedRule = rules.find((rule) => {
        const ruleFnParams = [values[key], null, { values }];
        let ruleFn;
        if (typeof rule === 'object') {
          ruleFn = rule.validator;
        } else {
          const ruleParts = rule.split(':');
          ruleFn = validations[ruleParts[0]];
          ruleFnParams[1] = ruleParts[1];
        }
        return ruleFn && !ruleFn(...ruleFnParams);
      });
      if (!failedRule) {
        return null;
      }
      const failedRuleName = typeof failedRule === 'object' ? failedRule.name : failedRule;
      const ruleName = failedRuleName.split(':')[0];
      const customErrorMessage = schema[key]?.errors?.[ruleName];
      const errorMessageConstructor = customErrorMessage || errorMessages[ruleName];
      let errorMessage = '';
      if (typeof errorMessageConstructor === 'function') {
        errorMessage = errorMessageConstructor(displayName, failedRuleName.split(':')[1]);
      } else {
        errorMessage = errorMessageConstructor || 'Error';
      }
      return [key, errorMessage];
    })
    .filter((e) => !!e);

  return errors;
}
export function serverValidateBySchema(...params) {
  const errors = validateBySchema(...params);
  if (errors.length) {
    throw new UnprocessableError(undefined, Object.fromEntries(errors));
  }
  return true;
}
export default function useForm(schema, defaultValues = {}, defaultOptions = {}) {
  const initialValues = useMemo(() => defaultValues, [defaultValues]);
  const options = useMemo(() => defaultOptions, [defaultOptions]);
  const initialStateRef = useRef(
    Object.keys(schema).reduce(
      (acc, key) => {
        if (!acc.values[key]) {
          acc.values[key] = schema[key]?.defaultValue || (initialValues[key] != null ? initialValues[key] : '');
        }
        if (!acc.errors[key]) {
          acc.errors[key] = '';
        }
        return acc;
      },
      {
        values: {},
        errors: {},
        isLoading: false,
      },
    ),
  );
  const [state, dispatch] = useReducer(reducer, initialStateRef.current);
  const handleChange = useCallback((ev) => {
    if (!ev?.target?.name) {
      return;
    }
    const {
      name, value, checked, type,
    } = ev.target;

    dispatch({
      type: 'change',
      name,
      value: type === 'checkbox' ? checked : value,
    });
  }, []);
  const setErrors = useCallback((errors) => {
    let errorsToDispatch = errors;
    if (typeof errors === 'string') {
      errorsToDispatch = [['_error', errors]];
    }
    dispatch({
      type: 'setErrors',
      errors: Object.fromEntries(errorsToDispatch),
    });
  }, []);
  const setValue = useCallback(
    (name, value, type = 'text') => {
      if (!name || value == null) {
        return;
      }
      handleChange({
        target: {
          name,
          value,
          checked: value,
          type,
        },
      });
    },
    [handleChange],
  );

  const validate = useCallback(
    (validateOptions) => {
      const errors = validateBySchema(schema, state.values, validateOptions);
      if (errors.length) {
        setErrors(errors);
      }
      return !errors.length;
    },
    [schema, setErrors, state.values],
  );

  const getInputProps = useCallback(
    (fieldName, defaultValue) => {
      const props = {
        name: fieldName,
        error: state.errors[fieldName],
        value: state.values[fieldName],
        onChange: handleChange,
      };
      if (defaultValue != null) {
        props.checked = state.values[fieldName] === defaultValue;
      }
      return props;
    },
    [state.errors, state.values, handleChange],
  );

  const reset = useCallback(() => {
    dispatch({
      type: 'setInitialState',
      payload: initialStateRef.current,
    });
  }, []);
  const submit = useCallback(
    async (fn, submitOptions = {}) => {
      const isValid = validate(submitOptions);
      if (!isValid) {
        return { success: false };
      }
      try {
        dispatch({
          type: 'resetErrors',
        });
        dispatch({
          type: 'setIsLoading',
          payload: true,
        });
        const data = await fn();
        return { success: true, data };
      } catch (error) {
        const errorsFromServer = error.errors || {};
        let formError = error.message || '';
        if (!errorsFromServer && !formError) {
          formError = 'Something went wrong, please try again later';
        }
        setErrors(Object.entries({
          _error: formError,
          ...errorsFromServer,
        }));
        if (error.status === 401) {
          if (options.logout) {
            options.logout()
          }
          if (options?.redirectOnAuthFailed) {
            Router.replace('/login');
          }
        }
        submitOptions?.onError?.(error);
        return { success: false, error };
      } finally {
        dispatch({
          type: 'setIsLoading',
          payload: false,
        });
      }
    },
    [validate, options, setErrors],
  );
  return {
    values: state.values,
    errors: state.errors,
    isLoading: state.isLoading,
    setErrors,
    validate,
    handleChange,
    getInputProps,
    submit,
    reset,
    setValue,
  };
}
